import * as THREE from 'three';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useViewScoreStore } from '@/store/viewScoreStore';
import styles from './styles.module.scss';
import { WeaveButton } from '../UI/WeaveButton/WeaveButton';
import { handleAddScoreToRoomModal } from '../AddScoreToRoomModal/AddScoreToRoomModal';
import { StatsMap } from '@/workers/viewAnalysis.worker';
import { WorkflowProps } from '@/types/workflow';
import { calculateViewScore } from '@/services/score/view';
import { highlightWindowOnPoint, restoreWindowOnPoint } from '@/utils/geometryModification';
import { FormaTab, FormaTabs } from '../UI/FormaTabs';
import { useWorkflowStore } from '@/store/workflowStore';
import { WeaveTooltip } from '../UI/WeaveTooltip/WeaveTooltip';
import { HorizonContext } from '@/types/horizonContext';
import { getBuildingGraph } from '@/utils/visualization';
import { WeaveAccordion } from '../UI/WeaveAccordion/WeaveAccordion';

const CATEGORY_COLORS: Record<string, string> = {
	building: '#CCCCCC',
	vegetation: '#00FF00',
	water: '#0066FF',
	road: '#454545',
	terrain: '#AB8845',
	open: '#87CCff',
	unknown: '#FF00FF',
	landmark: '#FFD700', // Golden for landmarks
	park: '#4CAF50', // Green for parks
	paintedFeature: '#66E680', // Bright green for painted features
};

const CATEGORY_LABELS: Record<string, string> = {
	building: 'Buildings',
	vegetation: 'Trees & Vegetation',
	water: 'Water',
	road: 'Roads',
	terrain: 'Terrain',
	open: 'Open Sky',
	landmark: 'Landmarks',
	park: 'Parks',
	paintedFeature: 'Painted Features',
	unknown: 'Unknown',
};

export const ViewAnalysisStats = (props: {
	workflowProps: WorkflowProps;
	sceneRef: React.MutableRefObject<THREE.Scene | null>;
	selectedBuildingId: string | null;
}) => {
	const { sceneRef, workflowProps, selectedBuildingId } = props;
	const { currentStage, workflowData, setWorkflowData } = useWorkflowStore();
	const { viewData, viewSettingsByPoint } = useViewScoreStore();
	const [levelHeights, setLevelHeights] = useState<number[]>([]);
	const [buildingBaseZ, setBuildingBaseZ] = useState<number | null>(null);
	// Aggregate stats from all windows
	const aggregatedStats: Record<
		string,
		{
			count: number;
			averageDistance: number;
			horizontalCount: number;
		}
	> = {};

	let totalRays = 0;

	Object.values(viewData).forEach((windowData) => {
		Object.entries(windowData).forEach(([category, stats]) => {
			if (!aggregatedStats[category]) {
				aggregatedStats[category] = {
					count: 0,
					averageDistance: 0,
					horizontalCount: 0,
				};
			}
			aggregatedStats[category].count += stats.count;
			aggregatedStats[category].averageDistance += stats.averageDistance * stats.count;
			aggregatedStats[category].horizontalCount += stats.horizontalCount;
			totalRays += stats.count;
		});
	});

	// Calculate weighted averages
	Object.keys(aggregatedStats).forEach((category) => {
		if (aggregatedStats[category].count > 0) {
			aggregatedStats[category].averageDistance /= aggregatedStats[category].count;
		}
	});

	// Sort by count descending
	const sortedCategories = Object.entries(aggregatedStats).sort(
		([, a], [, b]) => b.count - a.count
	);

	const handleAddToRoom = async (
		data: Record<string, StatsMap>,
		pointKey: string,
		settings?: { horizonContexts: HorizonContext[]; isBeyondHorizonEnabled: boolean }
	) => {
		const resolvedWorkflowProps: WorkflowProps =
			workflowProps ?? {
				workflowData,
				onDataChange: (payload: { data: unknown }) => setWorkflowData(2, payload),
				activeSection: 2,
				sectionLabel: 'View Score',
			};

		let closestRoomFillMesh: THREE.Mesh | undefined;
		if (sceneRef.current && !pointKey.startsWith('facade_')) {
			const coords = pointKey.split(',').map(parseFloat);
			if (coords.length === 3 && coords.every((n) => Number.isFinite(n))) {
				const point = new THREE.Vector3(coords[0], coords[1], coords[2]);
				let closestDistance = Infinity;

				sceneRef.current.traverse((child) => {
					if (child.userData.isRoomFloorFill && child instanceof THREE.Mesh) {
						const distance = child.position.distanceTo(point);
						if (distance < closestDistance) {
							closestDistance = distance;
							closestRoomFillMesh = child;
						}
					}
				});
			}
		}

		handleAddScoreToRoomModal(data, resolvedWorkflowProps, settings, closestRoomFillMesh);
	};

	const handleMouseEnter = (point: string) => {
		highlightWindowOnPoint(point, sceneRef.current);
	};

	const handleMouseLeave = (point: string) => {
		restoreWindowOnPoint(point, sceneRef.current);
	};

	useEffect(() => {
		let isCancelled = false;

		const loadSelectedBuildingLevels = async () => {
			if (!sceneRef.current || !selectedBuildingId) {
				setLevelHeights([]);
				setBuildingBaseZ(null);
				return;
			}

			const building = sceneRef.current.getObjectByProperty('uuid', selectedBuildingId);
			if (!building) {
				setLevelHeights([]);
				setBuildingBaseZ(null);
				return;
			}

			const bbox = new THREE.Box3().setFromObject(building);
			const baseZ = Number.isFinite(bbox.min.z) ? bbox.min.z : null;
			if (!isCancelled) {
				setBuildingBaseZ(baseZ);
			}

			try {
				const graphBuilding = await getBuildingGraph(building);
				const heights = (graphBuilding as { data?: { levels?: Array<{ height?: number }> } } | undefined)
					?.data?.levels
					?.map((level) => Number(level.height))
					.filter((height) => Number.isFinite(height) && height > 0) as number[] | undefined;

				if (!isCancelled) {
					setLevelHeights(heights ?? []);
				}
			} catch (error) {
				console.warn('Failed to load building graph for floor grouping:', error);
				if (!isCancelled) {
					setLevelHeights([]);
				}
			}
		};

		loadSelectedBuildingLevels();

		return () => {
			isCancelled = true;
		};
	}, [sceneRef, selectedBuildingId]);

	const parsePointZ = useCallback((pointKey: string): number | null => {
		if (pointKey.startsWith('facade_')) {
			const parts = pointKey.replace('facade_', '').split('_').map(Number);
			return parts.length === 3 && parts.every((value) => Number.isFinite(value)) ? parts[2] : null;
		}

		const parts = pointKey.split(',').map(Number);
		return parts.length === 3 && parts.every((value) => Number.isFinite(value)) ? parts[2] : null;
	}, []);

	const resolveFloorIndex = useCallback((z: number | null): number | null => {
		if (z === null || buildingBaseZ === null || levelHeights.length === 0) {
			return null;
		}

		let cumulativeZ = buildingBaseZ;
		for (let i = 0; i < levelHeights.length; i++) {
			cumulativeZ += levelHeights[i];
			if (z <= cumulativeZ) {
				return i;
			}
		}

		return levelHeights.length - 1;
	}, [buildingBaseZ, levelHeights]);

	const groupedViewEntries = useMemo(() => {
		const grouped = new Map<string, Array<[string, Record<string, StatsMap>]>>();
		const entries = Object.entries(viewData) as Array<[string, Record<string, StatsMap>]>;

		entries.forEach(([pointKey, stats]) => {
			const floorIndex = resolveFloorIndex(parsePointZ(pointKey));
			const floorLabel = floorIndex === null ? 'Unassigned Floor' : `Floor ${floorIndex}`;
			const existing = grouped.get(floorLabel);
			if (existing) {
				existing.push([pointKey, stats]);
			} else {
				grouped.set(floorLabel, [[pointKey, stats]]);
			}
		});

		return Array.from(grouped.entries()).sort((a, b) => {
			if (a[0] === 'Unassigned Floor') return 1;
			if (b[0] === 'Unassigned Floor') return -1;
			const aNum = Number(a[0].replace('Floor ', ''));
			const bNum = Number(b[0].replace('Floor ', ''));
			return aNum - bNum;
		});
	}, [viewData, parsePointZ, resolveFloorIndex]);

	if (sortedCategories.length === 0) {
		return <div>Run view analysis from windows or enable Facade View Mode to analyze building walls</div>;
	}

	let windowIndex = 0;
	let facadeIndex = 0;
	return (
		<div>
			<FormaTabs gap={8} density="medium">
				<FormaTab for="aggregate" label="Overall Summary" />
				<div id="aggregate" slot="content">
					<div className={styles.aggregateView}>
						<div className={styles.legend}>
							{sortedCategories.map(([category, stats]) => {
								const percentage = ((stats.count / totalRays) * 100).toFixed(1);
								const color = CATEGORY_COLORS[category] || CATEGORY_COLORS.unknown;
								const label = CATEGORY_LABELS[category] || category;

								return (
									<div key={category} className={styles.legendItem}>
										<div className={styles.legendRow}>
											<div
												className={styles.colorBox}
												style={{ backgroundColor: color }}
											/>
											<span className={styles.categoryName}>{label}</span>
											<span className={styles.percentage}>{percentage}%</span>
										</div>
										<div className={styles.details}>
											<span className={styles.detailItem}>
												{stats.count} rays
											</span>
											<span className={styles.detailItem}>
												Avg dist: {stats.averageDistance.toFixed(0)}m
											</span>
											{stats.horizontalCount > 0 && (
												<span className={styles.detailItem}>
													{stats.horizontalCount} at eye level
												</span>
											)}
										</div>
									</div>
								);
							})}
						</div>
						<div className={styles.summary}>
							<div className={styles.summaryItem}>
								<span className={styles.summaryLabel}>Total rays analyzed: </span>
								<span className={styles.summaryValue}>
									{totalRays.toLocaleString()}
								</span>
							</div>
							<div className={styles.summaryItem}>
								<span className={styles.summaryLabel}>View points analyzed:</span>
								<span className={styles.summaryValue}>
									{Object.keys(viewData).length}
								</span>
							</div>
						</div>
					</div>
				</div>
				<FormaTab for="byWindow" label="By View Point" />
				<div id="byWindow" slot="content" className={styles.byWindowContent}>
					<div className={styles.windowBreakdown}>
						{groupedViewEntries.map(([floorLabel, floorEntries], floorIndex) => (
							<WeaveAccordion
								key={floorLabel}
								label={floorLabel}
								expanded={floorIndex === 0}
								indicatorposition="right"
							>
								<div className={styles.windowCardsContainer}>
									{floorEntries.map(([windowKey, windowStats]) => {
										const windowRays = Object.values(windowStats).reduce(
											(sum, stat) => sum + stat.count,
											0
										);
										const sortedWindowCategories = Object.entries(windowStats).sort(
											([, a], [, b]) => b.count - a.count
										);

										const settingsAtAnalysis = viewSettingsByPoint[windowKey];
										const viewScore = calculateViewScore(
											windowStats,
											settingsAtAnalysis?.horizonContexts ?? [],
											settingsAtAnalysis?.isBeyondHorizonEnabled ?? false
										);

										const isFacadePoint = windowKey.startsWith('facade_');
										const displayLabel = isFacadePoint ? 'Facade Point' : 'Window';

										if (isFacadePoint) {
											facadeIndex += 1;
										} else {
											windowIndex += 1;
										}

										return (
											<div
												key={windowKey}
												className={styles.windowCard}
												onMouseEnter={() => handleMouseEnter(windowKey)}
												onMouseLeave={() => handleMouseLeave(windowKey)}
											>
												<div className={styles.windowHeader}>
													<div className={styles.windowTitle}>
														<span className={styles.windowNumber}>
															{displayLabel} {isFacadePoint ? facadeIndex : windowIndex}
														</span>
													</div>
													<div className={styles.windowMeta}>
														<span className={styles.windowScore}>
															Score: {viewScore.totalScore.toFixed(2)}
														</span>
													</div>
												</div>

												<div className={styles.windowStats}>
													{sortedWindowCategories.map(([category, stats]) => {
														const percentage = (
															(stats.count / windowRays) *
															100
														).toFixed(1);
														const color =
															CATEGORY_COLORS[category] ||
															CATEGORY_COLORS.unknown;
														const label = CATEGORY_LABELS[category] || category;

														return (
															<div
																key={category}
																className={styles.windowStatItem}
															>
																<div className={styles.windowStatRow}>
																	<div
																		className={styles.colorDot}
																		style={{ backgroundColor: color }}
																	/>
																	<span className={styles.windowStatLabel}>
																		{label}
																	</span>
																	<span className={styles.windowStatValue}>
																		{percentage}%
																	</span>
																</div>
															</div>
														);
													})}
													{viewScore.beyondHorizonEnabled && (
														<div className={styles.windowStatItem}>
															<span className={styles.windowStatLabel}>
																Bonus:
															</span>
															<span className={styles.windowStatValue}>
																+{viewScore.beyondHorizonBonus.toFixed(1)}
															</span>
														</div>
													)}
												</div>
												<WeaveTooltip
													className={styles.addToARoomButton}
													text={currentStage !== 'design_development'
														? 'Available in Gate 3'
														: `Add this ${isFacadePoint ? 'facade point' : 'window'}'s view score to a room`
													}>
													<WeaveButton
														onClick={() => handleAddToRoom(windowStats, windowKey, settingsAtAnalysis)}
														disabled={currentStage !== 'design_development'}
													>
														Add to Room
													</WeaveButton>
												</WeaveTooltip>
											</div>
										);
									})}
								</div>
							</WeaveAccordion>
						))}
					</div>
				</div>
			</FormaTabs>
		</div>
	);
};
